import React, { FC, PropsWithChildren, useCallback, useEffect } from 'react';
import { Button } from '@ui/button';
import { DropdownArrow } from '@ui/dropdown/DropdownArrow';
import { InputStyleType } from '@uiTypes/InputStyleType';
import { ButtonStyleType } from '@uiTypes/ButtonStyleType';
import { dropdownGetThunk } from '@legacyApp/client/store/dropdown/thunks/dropdownGet.thunk';
import { useOnUnmount } from '@common/hooks/useOnUnmount';
import { useDispatchCallback } from '@legacyApp/hooks/store/useAppDispatch';
import { DropdownContentData } from '@legacyApp/client/store/dropdown/dropdown.reducer';
import { useDropdownActiveSelector } from '@legacyApp/client/store/dropdown/selectors/useDropdownActiveSelector';
import { dropdownContentEqualityFn } from '@legacyApp/client/store/dropdown/methods/dropdownContentEqualityFn';
import {
	dropdownAdd,
	dropdownDelete,
	dropdownToggle,
} from '../../store/dropdown/dropdown.actions';
import { Trans } from '../../modules/translation/translate';
import { DropdownStyle } from './dropdown.style';

export interface DropdownProps extends DropdownContentData {
	active?: string;
	id: string; // isRequired
	help?: any;
	localTheme?: {
		error?: boolean;
		default?: boolean;
	};
}

export const Dropdown: FC<PropsWithChildren<DropdownProps>> = (props) => {
	const active = useDropdownActiveSelector(props.id);
	const close = useDispatchCallback(dropdownDelete);
	const add = useDispatchCallback(dropdownAdd);
	const toggle = useDispatchCallback(dropdownToggle);
	const get = useDispatchCallback(dropdownGetThunk);

	const addContent = useCallback(() => {
		if (!props.items) {
			return;
		}
		const lastContent: DropdownContentData = get(props.id);
		const content: DropdownContentData = {
			scrollElement: props.scrollElement,
			items: props.items,
			title: props.title,
			onClick: props.onClick,
			width: props.width,
			className: props.className,
			css: props.css,
			trans: props.trans,
			itemElement: props.itemElement,
		};
		if (lastContent && dropdownContentEqualityFn(content, lastContent)) {
			return;
		}
		add({
			id: props.id,
			content: content,
		});
	}, [add, get, props]);

	const toggleList = () => {
		if (props.items) {
			toggle({
				id: props.id,
				bool: !active,
			});
		}
	};

	useOnUnmount(() => {
		if (props.items) {
			close(props.id);
		}
	});

	useEffect(() => {
		addContent();
	}, [addContent]);

	return (
		<DropdownStyle
			localTheme={props.localTheme}
			data-id={props.id}
			onClick={!props.children ? undefined : toggleList}
			className={props.className}
		>
			{props.children ? (
				props.children
			) : (
				<Button
					onClick={toggleList}
					name="dropdown-active"
					styleType={ButtonStyleType.SECONDARY}
					borderType={InputStyleType.SQUARE}
					className="icon-wrapper"
					label={
						props.trans ? (
							<Trans
								label={props.active}
								namespace={
									typeof props.trans === 'boolean' ? undefined : props.trans
								}
							/>
						) : (
							props.active
						)
					}
				>
					{!!props.items && <DropdownArrow />}
				</Button>
			)}
			{props.help}
		</DropdownStyle>
	);
};
